
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import InternalLayout from '@/components/internal/InternalLayout';
import { Link } from 'react-router-dom';
import { 
  MessageCircle, 
  Calendar, 
  User, 
  Star,
  TrendingUp,
  Clock,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const Portal = () => {
  const membershipInfo = {
    tier: 'Platinum',
    creditsBalance: 127.50,
    nextDeposit: '2024-12-15',
    depositAmount: 50
  };

  const juvJourney = {
    currentPhase: 'Maintain & Enhance',
    progress: 75,
    totalSessions: 12,
    nextGoal: 'Skin Texture Optimization'
  };

  const treatmentPlan = [
    { id: 1, treatment: 'Botox Touch-up', date: '2024-12-15', status: 'scheduled', provider: 'Dr. Sarah Chen' },
    { id: 2, treatment: 'HydraFacial', date: '2024-12-22', status: 'recommended', provider: 'Ashley M.' },
    { id: 3, treatment: 'Skin Boosters', date: '2025-01-05', status: 'planned', provider: 'Dr. Sarah Chen' }
  ];

  const recentServices = [
    { id: 1, service: 'Lip Filler Consultation', date: '2024-11-18', rating: 5, credits: 45 },
    { id: 2, service: 'Botox Treatment', date: '2024-10-25', rating: 5, credits: 35 },
    { id: 3, service: 'HydraFacial Classic', date: '2024-10-10', rating: 4, credits: 25 }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return { backgroundColor: '#A55C52', color: 'white' };
      case 'recommended': return { backgroundColor: '#CDA79D', color: '#5C322B' };
      case 'planned': return { backgroundColor: 'rgba(242, 217, 211, 0.5)', color: '#5C322B' };
      default: return { backgroundColor: '#CDA79D', color: '#5C322B' };
    }
  };

  return (
    <InternalLayout 
      title="JUV Concierge Portal"
      subtitle="Your personalized beauty journey dashboard"
    >
      <div className="space-y-8">
        {/* Concierge Navigation */}
        <div className="flex flex-wrap gap-4">
          <Button 
            variant="outline" 
            className="hover:bg-white/80"
            style={{ borderColor: '#A55C52', color: '#A55C52' }}
          >
            <User className="h-4 w-4 mr-2" />
            JUV Concierge
          </Button>
          <Button 
            variant="outline" 
            className="hover:bg-white/80"
            style={{ borderColor: '#CDA79D', color: '#5C322B' }}
          >
            <Calendar className="h-4 w-4 mr-2" />
            Treatment Plan
          </Button>
          <Button 
            variant="outline" 
            className="hover:bg-white/80"
            style={{ borderColor: '#5C322B', color: '#5C322B' }}
          >
            <Star className="h-4 w-4 mr-2" />
            Membership Info
          </Button>
        </div>

        {/* Membership & Credits Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="text-white" style={{ background: 'linear-gradient(to bottom right, #A55C52, #5C322B)' }}>
            <CardHeader>
              <CardTitle className="font-juv-heading flex items-center">
                <Star className="h-6 w-6 mr-2" />
                {membershipInfo.tier} Member
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-white/80">JUV Credits</span>
                  <span className="text-2xl font-bold">${membershipInfo.creditsBalance.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-white/80">Next Deposit</span>
                  <span>${membershipInfo.depositAmount} on {membershipInfo.nextDeposit}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
            <CardHeader>
              <CardTitle className="font-juv-heading flex items-center" style={{ color: '#5C322B' }}>
                <TrendingUp className="h-6 w-6 mr-2" style={{ color: '#A55C52' }} />
                JUV Journey Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span style={{ color: 'rgba(92, 50, 43, 0.7)' }}>Current Phase</span>
                    <span className="font-medium" style={{ color: '#5C322B' }}>{juvJourney.progress}%</span>
                  </div>
                  <Progress value={juvJourney.progress} className="h-3" />
                </div>
                <div className="space-y-2">
                  <p className="font-medium" style={{ color: '#5C322B' }}>{juvJourney.currentPhase}</p>
                  <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                    {juvJourney.totalSessions} sessions completed • Next goal: {juvJourney.nextGoal}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Treatment Plan */}
        <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
          <CardHeader>
            <CardTitle className="font-juv-heading flex items-center" style={{ color: '#5C322B' }}>
              <Calendar className="h-6 w-6 mr-2" style={{ color: '#A55C52' }} />
              Your Treatment Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {treatmentPlan.map((plan) => (
                <div key={plan.id} className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'rgba(242, 217, 211, 0.3)' }}>
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white" style={{ background: 'linear-gradient(135deg, #A55C52, #5C322B)' }}>
                      <Calendar className="h-5 w-5" />
                    </div>
                    <div>
                      <h4 className="font-medium" style={{ color: '#5C322B' }}>{plan.treatment}</h4>
                      <p className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>{plan.date} • {plan.provider}</p>
                    </div>
                  </div>
                  <Badge style={getStatusColor(plan.status)}>
                    {plan.status}
                  </Badge>
                </div>
              ))}
              <Button 
                className="w-full text-white hover:opacity-90 transition-colors"
                style={{ backgroundColor: '#A55C52' }}
              >
                Book Next Treatment
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Service History */}
          <Card className="bg-white shadow-luxury" style={{ borderColor: 'rgba(205, 167, 157, 0.3)' }}>
            <CardHeader>
              <CardTitle className="font-juv-heading flex items-center" style={{ color: '#5C322B' }}>
                <Clock className="h-5 w-5 mr-2" style={{ color: '#A55C52' }} />
                Service History
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentServices.map((service) => (
                  <div key={service.id} className="flex items-center justify-between p-4 rounded-lg" style={{ backgroundColor: 'rgba(242, 217, 211, 0.3)' }}>
                    <div>
                      <h4 className="font-medium" style={{ color: '#5C322B' }}>{service.service}</h4>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-sm" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>{service.date}</span>
                        <div className="flex items-center">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`h-3 w-3 ${i < service.rating ? 'fill-current' : ''}`}
                              style={{ color: i < service.rating ? '#A55C52' : '#CDA79D' }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium" style={{ color: '#A55C52' }}>${service.credits} credits</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Concierge Chat */}
          <Card className="shadow-luxury" style={{ 
            background: 'linear-gradient(to bottom right, rgba(242, 217, 211, 0.3), #ffffff)',
            borderColor: 'rgba(205, 167, 157, 0.3)'
          }}>
            <CardHeader>
              <CardTitle className="font-juv-heading flex items-center" style={{ color: '#5C322B' }}>
                <MessageCircle className="h-5 w-5 mr-2" style={{ color: '#A55C52' }} />
                Concierge Support
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-white rounded-lg border" style={{ borderColor: 'rgba(205, 167, 157, 0.2)' }}>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white" style={{ backgroundColor: '#A55C52' }}>
                    <User className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#5C322B' }}>Sarah, your concierge</p>
                    <p className="text-sm mt-1" style={{ color: 'rgba(92, 50, 43, 0.7)' }}>
                      "Hi! Your skin is looking amazing. Ready to book your next HydraFacial?"
                    </p>
                  </div>
                </div>
              </div>
              
              <Button className="w-full text-white hover:opacity-90 transition-all duration-300" style={{ background: 'linear-gradient(135deg, #A55C52, #5C322B)' }}>
                <MessageCircle className="h-4 w-4 mr-2" />
                Chat with Concierge
              </Button>
              
              <div className="text-center">
                <Button 
                  variant="outline" 
                  size="sm"
                  className="hover:bg-white/80"
                  style={{ borderColor: 'rgba(205, 167, 157, 0.5)', color: '#5C322B' }}
                >
                  Schedule Consultation Call
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </InternalLayout>
  );
};

export default Portal;
